import axios from 'axios';
import { User } from 'oidc-client-ts';
import * as Sentry from '@sentry/react';
import { useRef, useMemo, useEffect, useReducer, useCallback } from 'react';

import { AuthContext } from './auth-context';
import { AuthUserType, ActionMapType, AuthStateType, JWTContextType } from '../types';
import {
  getUser,
  Settings,
  removeUser,
  signinSilent,
  signOutRedirect,
  configureUserManager,
} from './oidc';

enum Types {
  INITIAL = 'INITIAL',
  LOGIN = 'LOGIN',
  REGISTER = 'REGISTER',
  REFRESH = 'REFRESH',
  LOGOUT = 'LOGOUT',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType | null;
  };
  [Types.LOGIN]: {
    user: AuthUserType | null;
  };
  [Types.REGISTER]: {
    user: AuthUserType | null;
  };
  [Types.LOGOUT]: undefined;
};

type ActionsType = ActionMapType<Payload>[keyof ActionMapType<Payload>];

// ----------------------------------------------------------------------

const initialState: AuthStateType = {
  user: null,
  loading: true,
};

const reducer = (state: AuthStateType, action: ActionsType) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGIN) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.REGISTER) {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === Types.LOGOUT) {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

/**
 * Props for the AuthProvider component.
 *
 * @typedef {Object} Props
 * @property {React.ReactNode} children - The child components to be rendered within the provider.
 * @property {string} currentUserEndpoint - The endpoint to fetch the current user's information.
 * @property {Settings} userManagerSettings - Configuration settings for the user manager.
 * @property {(user: AuthUserType, onLogin: () => void, onLogout: () => void) => void} [navigateAfterLogin] - Optional callback function to handle navigation after login.
 * One of `onLogin` or `onLogout` needs to be called within this function for the flow to continue.
 */
type Props = {
  children: React.ReactNode;
  currentUserEndpoint: string;
  userManagerSettings: Settings;
};

export function AuthProvider({ children, currentUserEndpoint, userManagerSettings }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setSentryUserContext = useCallback((user: AuthUserType | null) => {
    if (user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: `${user.firstName} ${user.lastName}`,
        buyerOrg: user.buyerOrg?.name,
        buyerSite: user.buyerSite?.name,
        buyerOrgInitiative: user.buyerOrg?.initiative,
      });
    } else {
      Sentry.setUser(null);
    }
  }, []);

  const fetchUser = useCallback(
    async (callbackData: User): Promise<AuthUserType> => {
      const res = await axios.get(currentUserEndpoint, {
        headers: {
          Authorization: `Bearer ${callbackData.access_token}`,
          'AC-Request-Id': crypto.randomUUID(),
        },
      });
      return res.data;
    },
    [currentUserEndpoint]
  );

  const login = useCallback(
    async (callbackData: User): Promise<AuthUserType> => {
      try {
        const userResponse = await fetchUser(callbackData);

        const user: AuthUserType = {
          ...callbackData,
          ...userResponse,
        };

        setSentryUserContext(user);

        dispatch({
          type: Types.LOGIN,
          payload: {
            user,
          },
        });

        return user;
      } catch (error) {
        console.error('Login error:', error);
        throw error; // Re-throw the error to be handled by the caller
      }
    },
    [fetchUser, setSentryUserContext]
  );

  // LOGOUT (silent - no redirect)
  const logoutSilent = useCallback(async () => {
    setSentryUserContext(null);
    await removeUser();
    dispatch({
      type: Types.LOGOUT,
    });
  }, [setSentryUserContext]);

  // LOGOUT
  const isLoggingOut = useRef(false);
  const logout = useCallback(async () => {
    // Single-flight: a burst of failing requests (e.g. an expired-session storm) must trigger
    // exactly one sign-out redirect, not one per in-flight request.
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    try {
      setSentryUserContext(null);
      // to handle ac-authority down delete user when logging out
      await removeUser();
      await signOutRedirect();
      dispatch({
        type: Types.LOGOUT,
      });
    } catch (error) {
      // Sign-out failed (e.g. ac-authority unreachable) — release the guard so logout can retry.
      isLoggingOut.current = false;
      throw error;
    }
  }, [setSentryUserContext]);

  const initialize = useCallback(async () => {
    try {
      const userManager = configureUserManager(userManagerSettings);

      const handleUserLoaded = async (userCallback: User) => {
        try {
          const userResponse = await fetchUser(userCallback);

          // watch out as roles can get overwritten here
          const user: AuthUserType = {
            ...userCallback,
            ...userResponse,
          };

          setSentryUserContext(user);

          dispatch({
            type: Types.INITIAL,
            payload: {
              user,
            },
          });
        } catch (error: any) {
          if (error?.response?.status === 401) {
            // TODO: improve
            try {
              await signinSilent();
            } catch (e) {
              // Probably refresh token expired - logout in that case
              logout();
            }
          } else {
            console.error('User loaded error:', error);
            dispatch({
              type: Types.INITIAL,
              payload: {
                user: null,
              },
            });
          }
        }
      };
      // TODO: remove event listener on unmount
      userManager.events.addUserLoaded(handleUserLoaded);

      userManager.events.addSilentRenewError(() => {
        logout();
      });
      const savedUser = await getUser();

      if (savedUser) {
        handleUserLoaded(savedUser);
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: Types.INITIAL,
        payload: {
          user: null,
        },
      });
    }
  }, [fetchUser, logout, setSentryUserContext, userManagerSettings]);

  useEffect(() => {
    initialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo<JWTContextType>(
    () => ({
      user: state.user,
      roles: state.user?.roles?.map((role) => role.name) || [],
      permissions:
        state.user?.roles?.flatMap(
          (role) => role.permissions?.map((permission) => permission.name) || []
        ) || [],
      fetchUser,
      method: 'jwt',
      loading: state.loading,
      authenticated: !!state.user,
      unauthenticated: !state.user,
      login,
      logout,
      logoutSilent,
    }),
    [state.user, state.loading, fetchUser, login, logout, logoutSilent]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
